import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { ChartLine, Plus, TrendingDown, TrendingUp } from 'lucide-react'
import { Area, AreaChart, CartesianGrid, useXAxisScale, XAxis, YAxis } from 'recharts'

import { priorPeriodLabel } from './filters/dateRange'
import type { DateFilter } from './filters/types'
import EmptyCard from './EmptyCard'
import { useUtcDayClock } from './useUtcDayClock'
import { type UsageChartDatum, withCurrentWeekProjection } from './weekProjection'

import type { WordsDictatedResponse } from '@/api/teamInsights'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn, formatAxisNumber, formatTooltipNumber } from '@/lib/utils'

const chartConfig = {
  words: { label: 'Words' },
  desktop: { label: 'Desktop', color: 'var(--chart-1)' },
  mobile: { label: 'Mobile', color: 'var(--chart-2)' },
} satisfies ChartConfig

// Tooltip rows read top-to-bottom to match the stacked graph: desktop sits on
// top of the stack (rendered last), so it leads, with mobile below.
const TOOLTIP_ORDER = ['desktop', 'mobile']

type Granularity = 'daily' | 'weekly'

// Backend sends weekly `period` as the ISO date of the week's Sunday
// (see WordsDictatedWeeklyPoint). Saturday is start + 6.
const parseWeekPeriod = (period: string): { start: Date; end: Date } | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(period)) return null
  const start = new Date(`${period}T00:00:00Z`)
  if (Number.isNaN(start.getTime())) return null
  const end = new Date(start)
  end.setUTCDate(start.getUTCDate() + 6)
  return { start, end }
}

// Filter Dates are already UTC-midnight (see filters/dateRange.ts: the picker
// speaks UTC calendar days because the CH backend buckets rows by UTC date).
// Compare directly — projecting via local getters would silently shift a day
// in any non-UTC zone.
const clampWeek = (
  week: { start: Date; end: Date },
  filterStart?: Date,
  filterEnd?: Date
): { start: Date; end: Date } => {
  let { start, end } = week
  if (filterStart && filterStart > start) start = filterStart
  if (filterEnd && filterEnd < end) end = filterEnd
  return { start, end }
}

const formatDay = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })

const formatDayWithYear = (d: Date) =>
  d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })

const formatTooltipLabel = (
  period: string,
  granularity: Granularity,
  filterStart?: Date,
  filterEnd?: Date,
  // The projected week covers days the filter has not reached, so label the whole week.
  fullWeek?: boolean
): string => {
  // Daily and weekly periods are both ``YYYY-MM-DD`` — only weekly should
  // expand into a Sun–Sat range. Daily renders the single day.
  if (granularity === 'weekly') {
    const week = parseWeekPeriod(period)
    if (week) {
      const { start, end } = clampWeek(week, filterStart, fullWeek ? undefined : filterEnd)
      const sameYear = start.getUTCFullYear() === end.getUTCFullYear()
      const startStr = sameYear ? formatDay(start) : formatDayWithYear(start)
      return `${startStr} – ${formatDayWithYear(end)}`
    }
  }
  const d = new Date(`${period}T00:00:00Z`)
  return formatDayWithYear(d)
}

// Axis ticks clamp to the filter range so a partial leftmost bar at e.g.
// "This month" reads "May 1" rather than the bar's natural "Apr 26".
// Adjacent ticks can't collapse here: only the leftmost / rightmost bars
// are ever clipped, and their neighbors stay on natural week-starts.
const formatAxisTick = (period: string, granularity: Granularity, filterStart?: Date, filterEnd?: Date): string => {
  if (granularity === 'weekly') {
    const week = parseWeekPeriod(period)
    if (week) return formatDay(clampWeek(week, filterStart, filterEnd).start)
  }
  return formatDay(new Date(`${period}T00:00:00Z`))
}

const FAR = 10_000

// Same points as the fill, so one spline: these clips are what make it solid then dashed.
const ProjectionClips = ({ id, anchorPeriod }: { id: string; anchorPeriod: string }) => {
  const xScale = useXAxisScale()
  const anchorX = xScale?.(anchorPeriod)
  // Until the axis is laid out, keep the whole solid stroke and none of the dashed one.
  const split = typeof anchorX === 'number' ? anchorX : FAR
  return (
    <defs>
      <clipPath id={`${id}-actual`}>
        <rect x={-FAR} y={-FAR} width={FAR + split} height={2 * FAR} />
      </clipPath>
      <clipPath id={`${id}-projected`}>
        <rect x={split} y={-FAR} width={FAR} height={2 * FAR} />
      </clipPath>
    </defs>
  )
}

interface WordsDictatedChartProps {
  response: WordsDictatedResponse
  // React Query's `dataUpdatedAt`: structural sharing keeps `timeseries` on a deep-equal refetch.
  dataUpdatedAt: number
  filterStart?: Date
  filterEnd?: Date
  dateFilter: DateFilter
  // Member detail reuses this chart for one user; the team-level "invite team
  // members" empty state doesn't apply, so we drop the description and CTA.
  singleMember?: boolean
}

const WordsDictatedChart = ({
  response,
  dataUpdatedAt,
  filterStart,
  filterEnd,
  dateFilter,
  singleMember,
}: WordsDictatedChartProps) => {
  const [granularity, setGranularity] = React.useState<Granularity>('weekly')
  const navigate = useNavigate()
  const clipId = React.useId().replace(/[^a-zA-Z0-9]/g, '')

  // Key the memo on the instants: a fresh `data` array replays recharts' entrance animation.
  const filterStartMs = filterStart?.getTime()
  const filterEndMs = filterEnd?.getTime()
  // The projection reads the clock, so the clock must be a dep — a mounted chart would freeze it.
  const utcDayWokeAt = useUtcDayClock()
  const data = React.useMemo<UsageChartDatum[]>(() => {
    if (granularity !== 'weekly') return response.timeseries.daily
    return withCurrentWeekProjection(response.timeseries.weekly, response.timeseries.daily, {
      // Clock = the fetch instant, so it pairs with the actuals; a Redis hit can be up to 1h older.
      now: new Date(Math.max(utcDayWokeAt, dataUpdatedAt)),
      filterStart: filterStartMs === undefined ? undefined : new Date(filterStartMs),
      filterEnd: filterEndMs === undefined ? undefined : new Date(filterEndMs),
    })
  }, [response.timeseries, granularity, utcDayWokeAt, dataUpdatedAt, filterStartMs, filterEndMs])

  const projectedIdx = data.findIndex((d) => d.isProjected === true)
  const anchorPeriod = projectedIdx > 0 ? data[projectedIdx - 1].period : undefined
  const actualClip = anchorPeriod === undefined ? undefined : `url(#${clipId}-actual)`
  const projectedClip = anchorPeriod === undefined ? undefined : `url(#${clipId}-projected)`

  if (response.total === 0) {
    return (
      <EmptyCard
        icon={<ChartLine className="size-5" />}
        title="No words dictated"
        description={singleMember ? undefined : 'Add team members to begin seeing more usage trends.'}
        cta={
          singleMember
            ? undefined
            : {
                label: 'Invite team members',
                icon: <Plus className="size-4" />,
                onClick: () => navigate('/app/team'),
              }
        }
      />
    )
  }

  // pct_change_vs_prev_period is in percent units (2 dp). When the filter
  // includes today (or has no end_date), the backend compares trailing 7d
  // vs prior 7d; when the filter excludes today, it compares the caller's
  // range to the equivalent prior duration. Null only when prior=0 with
  // non-zero current (mathematically undefined).
  const pctChange = response.pct_change_vs_prev_period ?? null
  const isPositive = pctChange !== null && pctChange > 0
  const isNegative = pctChange !== null && pctChange < 0
  const TrendIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : null
  const pctChangeLabel = priorPeriodLabel(dateFilter)

  return (
    <Card className="pt-0">
      <CardHeader className="flex items-center gap-4 space-y-0 border-b pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-4">
          <CardTitle className="text-xl font-semibold">{response.total.toLocaleString()} words dictated</CardTitle>
          {pctChange !== null && (
            <div
              className={cn(
                'flex items-center gap-1 text-[15px] font-semibold leading-5',
                isPositive ? 'text-[var(--green-600)]' : 'text-muted-foreground'
              )}
            >
              {TrendIcon && <TrendIcon className="size-5" />}
              <span>
                {Math.abs(pctChange)}% {pctChangeLabel}
              </span>
            </div>
          )}
        </div>
        <CardAction>
          <Select value={granularity} onValueChange={(v) => setGranularity(v as Granularity)}>
            <SelectTrigger className="w-[140px] rounded-lg text-[15px] leading-5" aria-label="Select granularity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="daily" className="rounded-lg">
                Daily view
              </SelectItem>
              <SelectItem value="weekly" className="rounded-lg">
                Weekly view
              </SelectItem>
            </SelectContent>
          </Select>
        </CardAction>
      </CardHeader>
      <CardContent className="pl-0 pr-4 py-0">
        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
          <AreaChart data={data} margin={{ top: 12, right: 12, left: 12, bottom: 0 }}>
            <defs>
              <linearGradient id="fillDesktop" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-desktop)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-desktop)" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="fillMobile" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-mobile)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-mobile)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            {anchorPeriod !== undefined && <ProjectionClips id={clipId} anchorPeriod={anchorPeriod} />}
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="period"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              interval="preserveStartEnd"
              tickFormatter={(value) => formatAxisTick(value as string, granularity, filterStart, filterEnd)}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={48}
              allowDecimals={false}
              domain={[0, 'auto']}
              tickFormatter={(value) => (value === 0 ? '' : formatAxisNumber(value as number))}
            />
            <ChartTooltip
              cursor={false}
              // Recharts orders the payload by <Area> render order, which is
              // pinned by the stack, and it ignores `itemSorter` for a custom
              // `content`. Sort the rows here instead so desktop leads.
              content={({ active, label, payload }) => {
                const row = payload[0]?.payload as UsageChartDatum | undefined
                const projectedWeek = row?.isProjected === true
                return (
                  <ChartTooltipContent
                    active={active}
                    label={label}
                    payload={
                      // Each series is drawn by three areas; only the solid
                      // stroke's entry keeps the series name, so it is the row.
                      [...payload]
                        .filter((item) => item.value != null && TOOLTIP_ORDER.includes(item.name as string))
                        .sort(
                          (a, b) => TOOLTIP_ORDER.indexOf(a.name as string) - TOOLTIP_ORDER.indexOf(b.name as string)
                        ) as React.ComponentProps<typeof ChartTooltipContent>['payload']
                    }
                    labelFormatter={(value) => {
                      const range = formatTooltipLabel(
                        value as string,
                        granularity,
                        filterStart,
                        filterEnd,
                        projectedWeek
                      )
                      if (!projectedWeek) return range
                      return (
                        <>
                          {range} <span className="text-muted-foreground font-normal">· projected</span>
                        </>
                      )
                    }}
                    total={() => {
                      const sum = (row?.desktop ?? 0) + (row?.mobile ?? 0)
                      if (projectedWeek) {
                        return [
                          { label: 'Projected total', value: formatTooltipNumber(sum) },
                          { label: 'Words so far', value: formatTooltipNumber(row?.actualSoFar ?? 0) },
                        ]
                      }
                      return { label: 'Total', value: formatTooltipNumber(sum) }
                    }}
                  />
                )
              }}
            />
            {/* Three areas per series share the same points, so one spline: the dash resumes where the clipped solid stops. */}
            <Area
              dataKey="mobile"
              name="mobileFill"
              type="monotone"
              fill="url(#fillMobile)"
              stroke="none"
              stackId="fill"
              legendType="none"
              activeDot={false}
            />
            <Area
              dataKey="desktop"
              name="desktopFill"
              type="monotone"
              fill="url(#fillDesktop)"
              stroke="none"
              stackId="fill"
              legendType="none"
              activeDot={false}
            />
            <Area
              dataKey="mobile"
              type="monotone"
              fill="none"
              stroke="var(--color-mobile)"
              stackId="actual"
              clipPath={actualClip}
            />
            <Area
              dataKey="desktop"
              type="monotone"
              fill="none"
              stroke="var(--color-desktop)"
              stackId="actual"
              clipPath={actualClip}
            />
            {projectedClip && (
              <Area
                dataKey="mobile"
                name="mobileProjected"
                type="monotone"
                fill="none"
                stroke="var(--color-mobile)"
                strokeDasharray="4 4"
                stackId="projected"
                legendType="none"
                activeDot={false}
                clipPath={projectedClip}
              />
            )}
            {projectedClip && (
              <Area
                dataKey="desktop"
                name="desktopProjected"
                type="monotone"
                fill="none"
                stroke="var(--color-desktop)"
                strokeDasharray="4 4"
                stackId="projected"
                legendType="none"
                activeDot={false}
                clipPath={projectedClip}
              />
            )}
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

export default WordsDictatedChart
